import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable, signal } from '@angular/core';

import { catchError, finalize, map, Observable, of, tap } from 'rxjs';

import { createClientActionOptions } from '@empathy/global/utils';
import { SurveyFeedback, SurveyProgress } from '@empathy/survey/base';

import { SurveyViewerService } from './survey-viewer.service';
import { EnvironmentConfig } from '../config/environment.config';
import { SurveyCompleteResponse } from '../models/survey-complete-response.interface';
import { SurveyNavigationContract } from '../models/survey-navigation-contract.interface';
import { ISurveyNavigationResponse } from '../models/survey-navigation-response.interface';
import { retrySurveyNavigation } from '../utils/retry-survey-navigation.function';

const BACK_NAVIGATION_COOLDOWN_MS = 300;

@Injectable({ providedIn: 'root' })
export class SurveyNavigationService implements SurveyNavigationContract {
  isGoingBack$ = signal(false);
  isSaving$ = signal(false);
  isCompleting$ = signal(false);

  private http = inject(HttpClient);
  private surveyViewerService = inject(SurveyViewerService);

  constructor(@Inject('environment') private environment: EnvironmentConfig) {}

  sendFeedback(inviteId: string, feedback: SurveyFeedback): Observable<boolean | null> {
    return this.http.post(`${this.environment.surveyEndPoint}${inviteId}/feedback`, feedback).pipe(
      map(() => true),
      catchError(() => of(null))
    );
  }

  goNext(): Observable<ISurveyNavigationResponse> {
    const options = createClientActionOptions();
    const progress = {
      ...this.mapProgress(),
      answerId: this.surveyViewerService.answerId$(),
      version: this.surveyViewerService.version$(),
      actionId: options.headers['X-Client-Action-Id']
    };
    this.isSaving$.set(true);
    return this.http.post<ISurveyNavigationResponse>(`${this.environment.surveyEndPoint}${this.surveyViewerService.inviteId$()}/save`, progress, options).pipe(
      retrySurveyNavigation(),
      finalize(() => this.isSaving$.set(false))
    );
  }

  goBack(currentPageId: string): Observable<ISurveyNavigationResponse> {
    this.isGoingBack$.set(true);
    let cooldownScheduled = false;
    const options = createClientActionOptions();
    return this.http
      .post<ISurveyNavigationResponse>(
        `${this.environment.surveyEndPoint}${this.surveyViewerService.inviteId$()}/back`,
        {
          answerId: this.surveyViewerService.answerId$(),
          version: this.surveyViewerService.version$(),
          actionId: options.headers['X-Client-Action-Id'],
          expectedCurrentPageId: currentPageId
        },
        options
      )
      .pipe(
        retrySurveyNavigation(),
        tap(() => {
          cooldownScheduled = true;
          setTimeout(() => this.isGoingBack$.set(false), BACK_NAVIGATION_COOLDOWN_MS);
        }),
        finalize(() => {
          if (!cooldownScheduled) {
            this.isGoingBack$.set(false);
          }
        })
      );
  }

  complete(): Observable<SurveyCompleteResponse> {
    this.isCompleting$.set(true);
    return this.http
      .post<SurveyCompleteResponse>(
        `${this.environment.surveyEndPoint}${this.surveyViewerService.inviteId$()}/complete`,
        { answerId: this.surveyViewerService.answerId$() },
        createClientActionOptions()
      )
      .pipe(
        retrySurveyNavigation(),
        finalize(() => this.isCompleting$.set(false))
      );
  }

  private mapProgress(): SurveyProgress {
    return new SurveyProgress(JSON.stringify(this.surveyViewerService.mapAnswers()), this.surveyViewerService.mapSubmittedQuestionIds());
  }
}
